import React from 'react';
import { Stack } from '../stack';
import { CategoryEntry } from './types';
import { semantic_colors } from '../../foundation';
import { useProviderConfig } from '../provider';
import { css } from '@emotion/react';
import { BaseText } from '../text';
import styled from '@emotion/styled';
import { CategoryItem } from './CategoryItem';

export interface CategoryListProps {
  level: number;
  items: CategoryEntry[];
  onItemSelect: (item: CategoryEntry) => void;
  onItemClick: (item: CategoryEntry) => void;
  getCheckState: (item: CategoryEntry) => { checked: boolean; indeterminate: boolean };
  isSelected: (item: CategoryEntry) => boolean;
  multiple?: boolean;
  disabled?: boolean;
  width?: number;
}

export const CategoryList = ({
  level,
  items,
  onItemSelect,
  onItemClick,
  getCheckState,
  isSelected,
  multiple = false,
  disabled = false,
  width = 240,
}: CategoryListProps) => {
  const { locale } = useProviderConfig();
  const listId = `category-list-level-${level}`;
  const headerId = `category-header-level-${level}`;

  return (
    <Stack
      direction='column'
      className='pds-category-selector-list'
      width={width}
      role='group'
      aria-labelledby={headerId}
      aria-level={level}
      css={css`
        flex: 1;
        &:not(:first-of-type) {
          border-left: 1px solid ${semantic_colors.border.primary};
        }
      `}
    >
      <Header id={headerId}>
        {level}
        {locale?.CategorySelector.levelCategory}
      </Header>
      <Article
        direction='column'
        align='center'
        height='100%'
        role={multiple ? 'group' : 'listbox'}
        aria-multiselectable={multiple}
        id={listId}
      >
        {items.length > 0 ? (
          items.map((item) => (
            <CategoryItem
              key={item.id}
              item={item}
              isSelected={isSelected(item)}
              onItemSelect={onItemSelect}
              onItemClick={onItemClick}
              getCheckState={getCheckState}
              multiple={multiple}
              disabled={disabled}
              level={level}
            />
          ))
        ) : (
          <Stack direction='row' height='100%' align='center' justify='center' aria-live='polite'>
            <BaseText alignment='center' kind='Body_13_Regular' color={semantic_colors.content.secondary}>
              {locale?.CategorySelector.selectParentCategoryLine1}
              <br />
              {locale?.CategorySelector.selectParentCategoryLine2}
            </BaseText>
          </Stack>
        )}
      </Article>
    </Stack>
  );
};

const Header = styled(BaseText)`
  padding: 12px 16px;
  background: ${semantic_colors.fill.tertiary};
  border-bottom: 1px solid ${semantic_colors.border.primary};
`;

const Article = styled(Stack)`
  overflow-y: auto;
`;
